import React, { useEffect, useState } from "react";
import { X, ArrowRight, Lightning } from "@phosphor-icons/react";

const LS_KEY = "warpaint_onboarding_v1";

const STEPS = [
  {
    title: "Welcome to WarPaint DFS",
    body: "A rule-based lineup engine built to beat every slate's field — Thursday through Monday — it models how today's tournament field is actually building and carves out the structural gaps to exploit. Let's take a 30-second tour of the core systems.",
    highlight: null,
  },
  {
    title: "1 · Slate Selection",
    body: "This is your starting page. WarPaint maintains the current DraftKings slate catalog on our end — pick this week's slate from the quick-pick chips (Main, Showdown, Preseason, etc.), hit Apply, and you're loaded. Prefer manual? Drop your DraftKings salary CSV in the right panel and we take it from there. Either way, our 21-signal data stack (SportsGameOdds sharp-book player prop projections + ownership · Sleeper injuries & depth charts · Open-Meteo weather · nflverse/nflfastR defense rankings, EPA, byes, standings, schedules, refs, betting trends, and season-long form) takes over. Optional bonus: if you subscribe to a third-party projection service, drop their CSV for an extra market-consensus overlay.",
    highlight: '[data-testid="nav-uploads"]',
  },
  {
    title: "2 · Slate Intel",
    body: "Live SportsGameOdds Vegas lines (median across every book with vig stripped), nflverse defense-vs-position rankings, Sleeper injuries, Open-Meteo weather, depth charts, and Snap Surge breakout radar — and WarPaint's own vig-stripped player projections + market ownership — all recompute on the same schedule: 6 AM CT sharp-line pull, daily 5 PM CT refresh, and a 3-checkpoint Sunday injury sweep (10:30 / 11:00 / 11:30 CT) so your pool reflects the freshest inputs right up to the noon-CT lock.",
    highlight: '[data-testid="nav-data"]',
  },
  {
    title: "3 · Smart Optimizer & Manual Optimizer",
    body: "Smart Optimizer: pick MILLY / GPP / CASH / SHOWDOWN, enter your field size + max entries, and the rules scale to that contest (stack shapes, ownership window, copies cap, Vegas game scripts) while the field model keeps you clear of the crowd's builds. Manual Optimizer: your rules, your math — caps, stacks, exposures and IF/THEN group rules, plus our Fingerprint Variance and Cluster Rotation edge layers as opt-in checkboxes.",
    highlight: '[data-testid="nav-optimizer"]',
  },
  {
    title: "4 · Lineups → Download & Enter",
    body: "Every lineup shows Field-Win %, expected copies, and simulated ceiling / floor. The pre-lock banner fixes inactives in one click. When you're ready, click Download DK CSV, head to your DraftKings contest entry page, and upload the file in the DK bulk-import box — your full portfolio ships in one shot.",
    highlight: '[data-testid="nav-my-lineups"]',
  },
  {
    title: "You're Ready",
    body: "Every system runs off our proprietary WarPaint Framework — the rule engine that separates winners from chalk. Now go paint the field.",
    highlight: null,
  },
];

export default function Onboarding() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(LS_KEY)) {
      setTimeout(() => setOpen(true), 800);
    }
  }, []);

  const close = () => {
    localStorage.setItem(LS_KEY, "1");
    setOpen(false);
  };

  const next = () => {
    if (step >= STEPS.length - 1) close();
    else setStep(step + 1);
  };

  if (!open) return null;
  const s = STEPS[step];

  return (
    <div className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={close}>
      <div onClick={(e) => e.stopPropagation()}
        data-testid="onboarding-modal"
        className="bg-[#0a0a0a] border-2 border-[#39FF14] mm-ai-glow w-full max-w-lg">
        <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center bg-[#39FF14] text-black">
              <Lightning size={16} weight="fill" />
            </div>
            <div>
              <div className="overline">Onboarding · Step {step + 1} of {STEPS.length}</div>
              <div className="heading text-lg font-bold">{s.title}</div>
            </div>
          </div>
          <button onClick={close} data-testid="onboarding-skip" className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest">
            Skip
          </button>
        </div>
        <div className="p-6">
          <p className="text-sm text-zinc-300 leading-relaxed">{s.body}</p>
        </div>
        <div className="px-6 py-4 border-t border-zinc-800 flex items-center justify-between">
          <div className="flex gap-1">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 w-6 ${i === step ? "bg-[#39FF14]" : "bg-zinc-800"}`} />
            ))}
          </div>
          <button onClick={next} data-testid="onboarding-next"
            className="inline-flex items-center gap-2 bg-[#39FF14] text-black font-black uppercase tracking-widest text-xs py-2.5 px-5 hover:bg-[#2ee30d]">
            {step >= STEPS.length - 1 ? "Get Started" : "Next"}
            <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
}
